import React, { useState } from 'react';
import { CaretDown, CheckCircle, ImageSquare, Sparkle } from 'phosphor-react';
import useProductSuggestions from '../../use/useProductSuggestions';
import { CatalogProduct } from '../../types/entities';
import { PELES } from '../../types/enums';
import { reais } from '../../utils/moeda';
import ProductDetailModal from '../modals/ProductDetailModal';
import Skeleton, { SkeletonArea } from '../ui/Skeleton';
import Aviso from '../ui/Aviso';
import Carregando from '../ui/Carregando';
import LazyImage from '../ui/LazyImage';

/** Quantos produtos a gaveta mostra antes do "ver mais". */
const VISIVEIS = 3;

interface ProductDrawerProps {
  categoryId: string;
  /** "Etapa 2 de 5 · Hidratação" — vai para o cabeçalho da ficha. */
  stepLabel?: string;
  selectedProductId?: string | null;
  selectedUserProductId?: string | null;
  /** `null` remove o produto da etapa. */
  onSelect: (product: CatalogProduct | null) => void;
}

const mesmoProduto = (p: CatalogProduct, productId?: string | null, userProductId?: string | null) =>
  (p.id != null && p.id === productId) || (p.userProductId != null && p.userProductId === userProductId);

/**
 * Gaveta do cartão da etapa: os 3 primeiros produtos daquela categoria, com o que o
 * usuário já tem na frente. Toque no produto abre a ficha completa; "ver mais" abre a
 * categoria inteira — que já veio na mesma resposta, sem nova requisição.
 */
export default function ProductDrawer({
  categoryId,
  stepLabel,
  selectedProductId,
  selectedUserProductId,
  onSelect,
}: ProductDrawerProps) {
  const { products, combinaComVoce, loading, error } = useProductSuggestions(categoryId, true);
  const [expandido, setExpandido] = useState(false);
  const [detalhe, setDetalhe] = useState<CatalogProduct | null>(null);

  const visiveis = expandido ? products : products.slice(0, VISIVEIS);

  const esqueleto = (
    <SkeletonArea className="flex flex-col gap-2 pt-3" label="Carregando produtos">
      {Array.from({ length: VISIVEIS }, (_, i) => (
        <div key={i} className="flex items-center gap-3 p-2.5 rounded-xl border border-nude-100 dark:border-gray-700">
          <Skeleton className="w-12 h-12 rounded-lg flex-shrink-0" />
          <div className="flex-1 flex flex-col gap-2">
            <Skeleton className="h-3 w-2/3" />
            <Skeleton className="h-2.5 w-1/3" />
          </div>
        </div>
      ))}
    </SkeletonArea>
  );

  if (!loading && error) {
    return <Aviso mensagem={error} className="mt-3" />;
  }

  if (!loading && products.length === 0) {
    return (
      <p className="animate-enter py-4 text-xs text-nude-400 dark:text-gray-500">
        Ainda não temos produtos cadastrados para esta etapa.
      </p>
    );
  }

  return (
    <Carregando quando={loading} esqueleto={esqueleto}>
    <div className="flex flex-col gap-2 pt-3">
      {/* O `stagger` fica só na lista: o modal é irmão dela, não um item — se ficasse
          dentro, ganharia entrada de lista e ocuparia um nth-child. */}
      <div className="stagger flex flex-col gap-2">
      {visiveis.map((p) => {
        const escolhido = mesmoProduto(p, selectedProductId, selectedUserProductId);
        return (
          <button
            key={p.id ?? p.userProductId}
            onClick={() => setDetalhe(p)}
            className={`
              flex items-center gap-3 p-2.5 rounded-xl border text-left transition-colors active:scale-[0.97]
              ${escolhido
                ? 'border-blush-300 bg-blush-50/60 dark:bg-blush-900/20'
                : 'border-nude-100 dark:border-gray-700 bg-nude-50/50 dark:bg-gray-700/40 hover:border-blush-200'
              }
            `}
          >
            <LazyImage
              src={p.imageUrl}
              alt={p.name}
              className="w-12 h-12 rounded-lg bg-white dark:bg-gray-800 flex-shrink-0"
              imgClassName="w-full h-full object-contain"
              fallback={<ImageSquare className="w-5 h-5 text-nude-300 dark:text-gray-600" />}
            />

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 truncate">{p.name}</p>
                {p.owned && (
                  <span title="Você já tem" className="flex-shrink-0">
                    <Sparkle className="w-3 h-3 text-lavender-400" />
                  </span>
                )}
              </div>
              <p className="text-[11px] text-nude-400 dark:text-gray-500 truncate mt-0.5">
                {p.owned && p.recommendedFor.length === 0
                  ? 'Da sua prateleira'
                  : p.recommendedFor.map((t) => PELES[t]).join(' · ') || 'Sem indicação cadastrada'}
              </p>
            </div>

            <div className="flex-shrink-0 text-right">
              <p className="text-xs font-bold text-gray-700 dark:text-gray-200">
                {p.owned && p.price == null ? '—' : reais(p.price)}
              </p>
              {escolhido ? (
                <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-blush-500">
                  <CheckCircle className="w-3 h-3" weight="fill" />
                  na etapa
                </span>
              ) : (
                combinaComVoce(p) && (
                  <span className="text-[10px] font-semibold text-sage-600 dark:text-sage-400">
                    combina
                  </span>
                )
              )}
            </div>
          </button>
        );
      })}
      </div>

      {products.length > VISIVEIS && (
        <button
          onClick={() => setExpandido((v) => !v)}
          className="flex items-center justify-center gap-1 py-2 text-[11px] font-semibold text-blush-500 hover:text-blush-600 transition-colors"
        >
          {expandido ? 'Ver menos' : `Ver mais (${products.length - VISIVEIS})`}
          <CaretDown className={`w-3 h-3 transition-transform duration-200 ease-soft ${expandido ? 'rotate-180' : ''}`} />
        </button>
      )}

      <ProductDetailModal
        product={detalhe}
        stepLabel={stepLabel}
        selected={detalhe ? mesmoProduto(detalhe, selectedProductId, selectedUserProductId) : false}
        matchesSkin={detalhe ? combinaComVoce(detalhe) : false}
        onClose={() => setDetalhe(null)}
        onSelect={(p) => {
          onSelect(p);
          setDetalhe(null);
        }}
        onRemove={() => {
          onSelect(null);
          setDetalhe(null);
        }}
      />
    </div>
    </Carregando>
  );
}
